import { Template } from '../hooks/TemplateListModel';

enum ElementStatus {
  RESERVED = 'reserved',
  SEEN = 'seen',
}

/**
 * Validates template names by detecting duplicates and names that match reserved HTML elements.
 *
 * @param templates - Array of templates whose names are to be validated.
 *
 * @returns An object with an `error` property that contains:
 *          - `duplicates`: Names that appear more than once in the template list.
 *          - `reserved`: Names that are reserved HTML element names.
 */
export const validateTemplates = (templates: Template[]): { error: { duplicates: string[]; reserved: string[] } } => {
  const error = {
    duplicates: [] as string[],
    reserved: [] as string[],
  };

  templates.forEach(template => {
    if (RESERVED_HTML_ELEMENTS[template.name] === ElementStatus.RESERVED) {
      error.reserved.push(template.name);
    } else if (!RESERVED_HTML_ELEMENTS[template.name]) {
      RESERVED_HTML_ELEMENTS[template.name] = ElementStatus.SEEN;
    } else if (RESERVED_HTML_ELEMENTS[template.name] === ElementStatus.SEEN) {
      error.duplicates.push(template.name);
    }
  });

  return { error };
};

const RESERVED_HTML_ELEMENTS: Record<string, ElementStatus> = {
  a: ElementStatus.RESERVED,
  abbr: ElementStatus.RESERVED,
  address: ElementStatus.RESERVED,
  area: ElementStatus.RESERVED,
  article: ElementStatus.RESERVED,
  aside: ElementStatus.RESERVED,
  audio: ElementStatus.RESERVED,
  b: ElementStatus.RESERVED,
  base: ElementStatus.RESERVED,
  bdi: ElementStatus.RESERVED,
  bdo: ElementStatus.RESERVED,
  blockquote: ElementStatus.RESERVED,
  body: ElementStatus.RESERVED,
  br: ElementStatus.RESERVED,
  button: ElementStatus.RESERVED,
  canvas: ElementStatus.RESERVED,
  caption: ElementStatus.RESERVED,
  cite: ElementStatus.RESERVED,
  code: ElementStatus.RESERVED,
  col: ElementStatus.RESERVED,
  colgroup: ElementStatus.RESERVED,
  data: ElementStatus.RESERVED,
  datalist: ElementStatus.RESERVED,
  dd: ElementStatus.RESERVED,
  del: ElementStatus.RESERVED,
  details: ElementStatus.RESERVED,
  dfn: ElementStatus.RESERVED,
  dialog: ElementStatus.RESERVED,
  div: ElementStatus.RESERVED,
  dl: ElementStatus.RESERVED,
  dt: ElementStatus.RESERVED,
  em: ElementStatus.RESERVED,
  embed: ElementStatus.RESERVED,
  fieldset: ElementStatus.RESERVED,
  figcaption: ElementStatus.RESERVED,
  figure: ElementStatus.RESERVED,
  footer: ElementStatus.RESERVED,
  form: ElementStatus.RESERVED,
  h1: ElementStatus.RESERVED,
  h2: ElementStatus.RESERVED,
  h3: ElementStatus.RESERVED,
  h4: ElementStatus.RESERVED,
  h5: ElementStatus.RESERVED,
  h6: ElementStatus.RESERVED,
  head: ElementStatus.RESERVED,
  header: ElementStatus.RESERVED,
  hgroup: ElementStatus.RESERVED,
  hr: ElementStatus.RESERVED,
  html: ElementStatus.RESERVED,
  i: ElementStatus.RESERVED,
  iframe: ElementStatus.RESERVED,
  img: ElementStatus.RESERVED,
  input: ElementStatus.RESERVED,
  ins: ElementStatus.RESERVED,
  kbd: ElementStatus.RESERVED,
  label: ElementStatus.RESERVED,
  legend: ElementStatus.RESERVED,
  li: ElementStatus.RESERVED,
  link: ElementStatus.RESERVED,
  main: ElementStatus.RESERVED,
  map: ElementStatus.RESERVED,
  mark: ElementStatus.RESERVED,
  menu: ElementStatus.RESERVED,
  menuitem: ElementStatus.RESERVED,
  meta: ElementStatus.RESERVED,
  meter: ElementStatus.RESERVED,
  nav: ElementStatus.RESERVED,
  noscript: ElementStatus.RESERVED,
  object: ElementStatus.RESERVED,
  ol: ElementStatus.RESERVED,
  optgroup: ElementStatus.RESERVED,
  option: ElementStatus.RESERVED,
  output: ElementStatus.RESERVED,
  p: ElementStatus.RESERVED,
  param: ElementStatus.RESERVED,
  picture: ElementStatus.RESERVED,
  pre: ElementStatus.RESERVED,
  progress: ElementStatus.RESERVED,
  q: ElementStatus.RESERVED,
  rb: ElementStatus.RESERVED,
  rp: ElementStatus.RESERVED,
  rt: ElementStatus.RESERVED,
  rtc: ElementStatus.RESERVED,
  ruby: ElementStatus.RESERVED,
  s: ElementStatus.RESERVED,
  samp: ElementStatus.RESERVED,
  script: ElementStatus.RESERVED,
  section: ElementStatus.RESERVED,
  select: ElementStatus.RESERVED,
  small: ElementStatus.RESERVED,
  source: ElementStatus.RESERVED,
  span: ElementStatus.RESERVED,
  strong: ElementStatus.RESERVED,
  style: ElementStatus.RESERVED,
  sub: ElementStatus.RESERVED,
  summary: ElementStatus.RESERVED,
  sup: ElementStatus.RESERVED,
  table: ElementStatus.RESERVED,
  tbody: ElementStatus.RESERVED,
  td: ElementStatus.RESERVED,
  template: ElementStatus.RESERVED,
  textarea: ElementStatus.RESERVED,
  tfoot: ElementStatus.RESERVED,
  th: ElementStatus.RESERVED,
  thead: ElementStatus.RESERVED,
  time: ElementStatus.RESERVED,
  title: ElementStatus.RESERVED,
  tr: ElementStatus.RESERVED,
  track: ElementStatus.RESERVED,
  u: ElementStatus.RESERVED,
  ul: ElementStatus.RESERVED,
  var: ElementStatus.RESERVED,
  video: ElementStatus.RESERVED,
  wbr: ElementStatus.RESERVED,
};
